import { useEffect, useRef, useState } from "react";

type Props = {
  children: string;
  className?: string;
  /** Maximum font size in px (starting point). */
  maxFontSize?: number;
  /** Minimum font size in px (won't shrink below). */
  minFontSize?: number;
  /** Step in px used while shrinking. */
  step?: number;
};

/**
 * Renders a single-line label that auto-shrinks its font-size so the text
 * always fits the container width. Prevents wrapping and clipping across
 * languages with varying word lengths.
 */
export function AutoFitText({
  children,
  className,
  maxFontSize = 12,
  minFontSize = 8,
  step = 0.5,
}: Props) {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const fit = () => {
      const parent = el.parentElement;
      if (!parent) return;
      const available = parent.clientWidth;
      if (available <= 0) return;

      let size = maxFontSize;
      el.style.fontSize = `${size}px`;
      // Shrink until content fits or we hit the minimum.
      while (el.scrollWidth > available && size > minFontSize) {
        size = Math.max(minFontSize, size - step);
        el.style.fontSize = `${size}px`;
      }
      setFontSize(size);
    };

    fit();
    const ro = new ResizeObserver(fit);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [children, maxFontSize, minFontSize, step]);

  return (
    <span
      ref={containerRef}
      className={className}
      style={{ fontSize, whiteSpace: "nowrap", display: "block" }}
    >
      {children}
    </span>
  );
}
